

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Grid, Snackbar, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import { useAuth } from 'auth/Auth';
import { formatDate } from 'utils/DateTimeConversion';

const TravelInvoiceLineItemDetail = () => {
    const { id } = useParams();
    const { axiosInstance } = useAuth();
    const [lineItem, setLineItem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (id) {
            fetchLineItemDetail();
        }
    }, [id]);

    const fetchLineItemDetail = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/travel-invoice-line-items/${id}`);
            setLineItem(response.data);
        } catch (error) {
            console.error('Error fetching travel invoice line item details:', error);
            setSnackbar({ open: true, message: 'Error loading travel invoice line item details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (amount, currency = 'GBP') => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount));
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <DataLoadingSpinner title="Loading Travel Invoice Line Item Details" message="Please wait..." />
                
            </DashboardLayout>
        );
    }

    if (!lineItem) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={6}><MDTypography color="error">Travel Invoice Line Item not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    const mainInfo = {
        "Name": lineItem.name,
        "Invoice": lineItem.invoice?.name || lineItem.invoice_id || '-',
        "Order": lineItem.order?.name || lineItem.order_id || '-',
        "Customer": lineItem.customer?.name || lineItem.customer_id || '-',
        "Amount": formatCurrency(lineItem.amount, lineItem.currency),
        "Currency": lineItem.currency,
        "Status": lineItem.status,
        "Invoice Date": lineItem.invoice_date ? formatDate(lineItem.invoice_date) : '-',
        "Due Date": lineItem.due_date ? formatDate(lineItem.due_date) : '-',
        "Reference No": lineItem.ref_no || '-',
    };

    const renderDetailCard = (title, data) => (
        <MDBox m={3} p={3} borderRadius="lg" sx={{ backgroundColor: 'white', boxShadow: 3 }}>
            <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
                {title}
            </MDTypography>
            <Grid container spacing={3}>
                {Object.entries(data).map(([key, value]) => (
                    <Grid item xs={12} sm={6} key={key}>
                        <MDBox display="flex" alignItems="center">
                            <MDTypography variant="button" fontWeight="bold" color="text">
                                {key}:
                            </MDTypography>
                            <MDTypography fontSize="0.9rem" color="text" ml={1}>
                                {value}
                            </MDTypography>
                        </MDBox>
                    </Grid>
                ))}
            </Grid>
        </MDBox>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                                <MDTypography variant="h6" color="white">
                                    Travel Invoice Line Item Details: {lineItem.name}
                                </MDTypography>
                            </MDBox>
                            <MDBox>
                                {renderDetailCard('Line Item Information', mainInfo)}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelInvoiceLineItemDetail;

